import {isAdmin, isDemocollege, isDevEnv, isParent, isSelectiveAiDomains, isStudent, isTeacher} from 'common/HelperFunctions'

import type {CurriculumStatus} from '../api/curriculumApi'
import type {AssistantRole, AssistantTab} from '../types'

/** One-line switch for the Curriculum AI Assistant ("Learning Assistant"), a separate product. */
export const CURRICULUM_ASSISTANT_ENABLED = true

/**
 * One-line switch for where the data comes from:
 * - `api`: the curriculum API (`/api/v1/curriculum`). `GET /status` decides who sees it, so no
 *   environment check here (the backend switches a school on).
 * - `demo`: the built-in demo book (`data/demoBook.ts`), dev builds only, no backend needed.
 */
export const CURRICULUM_DATA_SOURCE = 'api' as 'api' | 'demo'

/**
 * Screens from the approved mockups that the curriculum API does not serve yet (practice, help at
 * home, class quizzes, lesson plans, insights, publisher view). They run on the demo book; set to
 * false to hide them.
 */
export const SHOW_DEMO_ONLY_TABS = false

/**
 * One-line switch for the frontend fixes from the 5-Nov bug sheet ("Curriculum Versi" tab):
 * - #1: no red dot on the bell when there are no notifications;
 * - #2: no "Explain simpler" under a reply that cites no page (greetings, casual messages);
 * - #3: stray "/" trimmed from unit and section titles;
 * - #5: the "From the book" box is left out when the answer already says the same words;
 * - #6: lists, headings and bold in answers laid out cleanly;
 * - #7: the Ask chat stays (per book and user) across tabs and pages until "New chat" or
 *   "Remove chat";
 * - #8: "Open page" opens the page on the same tab, with a skeleton while a page loads;
 * - #9: a unit without a cover shows its first page instead of nothing;
 * - #12: Read aloud turns into Stop while it plays;
 * - #17: the book's `[picture: parrot] — parrot` markers read as a clean list;
 * - #19: every page an answer cites can be opened on the side, not only the first.
 *
 * `false` brings back the 4-Nov behaviour.
 */
export const CURRICULUM_QA_NOV5 = true as boolean

/** Where SchoolRoutes and StudentRoutes mount the Learning Assistant. */
export const LEARNING_ASSISTANT_PATH = '/learning-assistant'

let cachedStatus: CurriculumStatus | null = null

/** Remembers the last `GET /status` answer for code outside React (the sidebar config). */
export const setCachedStatus = (status: CurriculumStatus) => {
  cachedStatus = status
}

/** Whether the route is registered at all (the page itself waits for `GET /status`). */
export const isCurriculumAssistantRouteOn = (): boolean =>
  CURRICULUM_ASSISTANT_ENABLED &&
  // the publisher demo runs on democollege only: no route, sidebar entry or status call elsewhere
  isDemocollege() &&
  !isSelectiveAiDomains() &&
  (CURRICULUM_DATA_SOURCE === 'api' || isDevEnv())

/**
 * Whether the sidebar shows the Learning Assistant: the route is on and, with the API, `GET /status`
 * said enabled (`useCurriculumStatus` in the sidebar fetches it and re-renders).
 */
export const isCurriculumAssistantEnabled = (): boolean =>
  isCurriculumAssistantRouteOn() && (CURRICULUM_DATA_SOURCE === 'demo' || cachedStatus?.enabled === true)

/**
 * The Learning Assistant persona for the signed-in user. Staff and admins see the principal view
 * (insights first); there is no separate principal user type in the portal.
 */
export const getAssistantRole = (): AssistantRole => {
  if (isStudent()) return 'student'
  if (isParent()) return 'parent'
  if (isTeacher()) return 'teacher'
  return 'principal'
}

/** Tabs served by the curriculum API; the rest run on the demo book. */
const ASK_TABS: AssistantTab[] = ['ask', 'photo', 'explain']
const MANAGE_TABS: AssistantTab[] = ['books']

/**
 * Tabs the user sees, in order; the first one is the landing tab. Ask tabs need `can_ask`, the
 * Book Library (`books`) needs `can_manage` (school admins: only they upload books, everyone uses
 * them). In demo mode the Book Library goes to admins.
 */
export const getAssistantTabs = (
  role: AssistantRole = getAssistantRole(),
  status: Pick<CurriculumStatus, 'can_ask' | 'can_manage'> = {can_ask: true, can_manage: isAdmin()}
): AssistantTab[] => {
  const byRole: Record<AssistantRole, AssistantTab[]> = {
    student: ['ask', 'photo', 'explain', 'practice'],
    parent: ['ask', 'photo', 'home-help'],
    teacher: ['ask', 'photo', 'quizzes', 'lessons', 'dashboard'],
    principal: ['dashboard', 'ask', 'publisher', 'books'],
  }
  const tabs = byRole[role].filter((tab) => {
    if (ASK_TABS.includes(tab)) return status.can_ask
    if (MANAGE_TABS.includes(tab)) return status.can_manage
    // With the API, the dashboard shows live data (DashboardView), not the demo insights.
    if (tab === 'dashboard' && CURRICULUM_DATA_SOURCE === 'api') return true
    return SHOW_DEMO_ONLY_TABS
  })
  // A manager who is not a principal-type user (unlikely) still reaches the Book Library.
  if (status.can_manage && !tabs.includes('books')) tabs.push('books')
  return tabs
}
